import { forwardRef, memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import toString from 'lodash/toString';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
// Material Icon imports
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

// ~2px epsilon absorbs sub-pixel rounding in scroll math.
const SCROLL_EPSILON = 2 as const;

// MUI's `.MuiInputBase-input` class forces this element to `display: block` (it wins over sx), so
// vertical centering is handled on the inner scroll box, not here.
const wrapperSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  minWidth: 0,
  height: '100%',
  overflow: 'hidden'
};

// Fill the field's full height and flex-center the single line, reproducing the native input's
// vertical centering so descenders stay visible (overflowX: 'auto' forces overflowY out of
// `visible`, which would otherwise clip a line box shorter than the glyph height).
const scrollBoxSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' }
};

const leftIndicatorSx: SxProps<Theme> = (theme) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  pr: 3,
  pointerEvents: 'none',
  color: 'text.secondary',
  background: `linear-gradient(to left, transparent, ${theme.palette.background.paper} 60%)`
});

const rightIndicatorSx: SxProps<Theme> = (theme) => ({
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  pl: 3,
  pointerEvents: 'none',
  color: 'text.secondary',
  background: `linear-gradient(to right, transparent, ${theme.palette.background.paper} 60%)`
});

type ScrollableTitleInputProps = {
  readonly value?: unknown;
  readonly className?: string;
  // for Storybook only: 1 → force right indicator, 2 → force both (jsdom computes no layout)
  readonly testCase?: number;
};

// MUI `inputComponent`: a non-focusable, horizontally scrollable renderer for the read-only title,
// with edge chevron indicators reflecting live scroll position. MUI may call `.focus()` on the
// forwarded ref — harmless on a div.
const ScrollableTitleInput = forwardRef<HTMLDivElement, ScrollableTitleInputProps>(({
  value,
  className,
  testCase
}, ref) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= SCROLL_EPSILON);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - SCROLL_EPSILON);
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, value]);

  const forced = testCase === 1 || testCase === 2;
  const showLeft = forced ? testCase === 2 : !atStart;
  const showRight = forced ? true : !atEnd;

  return (
    <Box ref={ref} className={className} sx={wrapperSx}>
      <Box ref={scrollRef} onScroll={updateScrollState} sx={scrollBoxSx}>
        {toString(value)}
      </Box>
      {showLeft ? (
        <Box aria-hidden sx={leftIndicatorSx}>
          <KeyboardArrowLeft fontSize="small"/>
        </Box>
      ) : undefined}
      {showRight ? (
        <Box aria-hidden sx={rightIndicatorSx}>
          <KeyboardArrowRight fontSize="small"/>
        </Box>
      ) : undefined}
    </Box>
  );
});

ScrollableTitleInput.displayName = 'ScrollableTitleInput';

export default memo(ScrollableTitleInput);
